/**
 * Rules for manufacturer barcodes, shared by the API and the web app.
 *
 * Only EAN-13 and UPC-A carry a check digit worth checking: a code of either type entered with a
 * wrong last digit is a typing mistake that would print a label nobody can scan against the box.
 * Codes already stored are never re-validated or rewritten — the rules apply to what is entered now.
 */

export type EnteredBarcodeType = 'EAN_13' | 'UPC_A' | 'CODE_128' | 'OTHER';

/** The GS1 mod-10 check: weights 3 and 1 alternate from the digit next to the check digit. */
export function gtinCheckDigitValid(code: string): boolean {
  if (!/^\d{8,14}$/.test(code)) return false;
  const digits = [...code].map(Number);
  const check = digits.pop() as number;
  const sum = digits
    .reverse()
    .reduce((total, digit, index) => total + digit * (index % 2 === 0 ? 3 : 1), 0);
  return (10 - (sum % 10)) % 10 === check;
}

/** The type a code most plausibly is when none was chosen: 13 digits EAN-13, 12 digits UPC-A, anything else OTHER. */
export function inferBarcodeType(code: string): 'EAN_13' | 'UPC_A' | 'OTHER' {
  if (/^\d{13}$/.test(code)) return 'EAN_13';
  if (/^\d{12}$/.test(code)) return 'UPC_A';
  return 'OTHER';
}

/** Why a newly entered code cannot be of this type, or null when it can. */
export function barcodeProblem(
  code: string,
  type: EnteredBarcodeType,
): 'EAN_13_FORMAT' | 'UPC_A_FORMAT' | 'CHECK_DIGIT' | null {
  if (type === 'EAN_13' && !/^\d{13}$/.test(code)) return 'EAN_13_FORMAT';
  if (type === 'UPC_A' && !/^\d{12}$/.test(code)) return 'UPC_A_FORMAT';
  if ((type === 'EAN_13' || type === 'UPC_A') && !gtinCheckDigitValid(code)) return 'CHECK_DIGIT';
  return null;
}
